import React, { useMemo, useState } from 'react';
import { usePayrollHoursText } from '../../hooks/usePayrollHoursText';
import { createPortal } from 'react-dom';
import { Clock, Calendar, ChevronDown, DollarSign, Plus, MapPin, Loader2 } from 'lucide-react';
import { format, isValid, parseISO } from 'date-fns';
import TimeclockLocationModal from '../timeclock/TimeclockLocationModal';
import { getPersonInitials } from '../../utils/personInitials';

function payrollStatusLabel(status?: string | null): string {
  if (status === 'PAID') return 'PAID';
  if (status === 'VOIDED') return 'VOIDED';
  return 'UNPAID';
}

function payrollStatusClass(status?: string | null): string {
  if (status === 'PAID') return 'bg-green-100 text-green-700';
  if (status === 'VOIDED') return 'bg-red-50 text-red-700';
  return 'bg-yellow-100 text-yellow-700';
}

interface PayrollEntry {
  id: string;
  resource_id: string;
  resource_first_name: string;
  resource_last_name: string;
  resource_email: string;
  compensation_type: string;
  flat_rate_amount: number | null;
  hourly_rate: number | null;
  rate_amount?: number | null;
  needs_review?: boolean;
  review_reason?: string | null;
  booking_id: string | null;
  booking_number: number | null;
  booking_title: string | null;
  start_date: string | null;
  end_date: string | null;
  entry_type: 'flat_rate' | 'timeclock' | 'commission' | 'bonus' | 'tip' | 'adjustment';
  hours: number;
  earnings: number;
  item_name: string | null;
  item_description: string | null;
  created_at: string;
  clock_in?: string;
  clock_out?: string;
  break_start?: string;
  break_end?: string;
  total_minutes?: number;
  notes?: string;
  status?: string;
  process_status: 'PAID' | 'UNPAID' | 'VOIDED';
  completed_at?: string;
}

interface PayrollResourceSummaryProps {
  resourceId: string;
  data: {
    resource: {
      first_name: string;
      last_name: string;
      email: string;
      compensation_type: string;
      flat_rate_amount: number | null;
      hourly_rate: number | null;
      avatar_url?: string;
    };
    timeclock: {
      entries: any[];
      totalHours: number;
      totalEarnings: number;
    };
    bookings: Record<string, {
      booking_number: number;
      booking_title: string;
      start_date: string;
      end_date: string;
      items: any[];
      totalHours: number;
      totalEarnings: number;
    }>;
    bonuses?: {
      entries: any[];
      totalEarnings: number;
    };
    tips?: {
      entries: any[];
      totalEarnings: number;
    };
    adjustments?: {
      entries: any[];
      totalEarnings: number;
    };
    needsReviewCount?: number;
    totalHours: number;
    totalEarnings: number;
  };
  onConfirmEntry?: (entryId: string) => Promise<void> | void;
  isExpanded: boolean;
  onToggle: () => void;
  onToggleBooking: (bookingId: string) => void;
  expandedBookings: Set<string>;
  onEditEntry: (entry: PayrollEntry) => void;
  onCreateEntry?: (resourceId: string) => void;
  onCreateBonus?: (resourceId: string) => void;
  isLoading?: boolean;
}

// Helper function to safely format dates
const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  if (!isValid(date)) return '-';
  return format(date, 'MMM d, yyyy');
};


export default function PayrollResourceSummary({
  resourceId,
  data,
  isExpanded,
  onToggle,
  onToggleBooking,
  expandedBookings,
  onEditEntry,
  onCreateEntry,
  onCreateBonus,
  onConfirmEntry,
  isLoading = false,
}: PayrollResourceSummaryProps) {
  const [locationEntry, setLocationEntry] = useState<any>(null);
  const hourMinutes = useMemo(() => {
    const values: number[] = [];
    let total = 0;
    for (const entry of data.timeclock.entries) {
      const mins = entry.total_minutes || 0;
      total += mins;
      values.push(mins);
      const overtime = (entry.overtime_minutes || 0) + (entry.double_time_minutes || 0);
      if (overtime > 0) values.push(overtime);
    }
    values.push(total);
    return values;
  }, [data.timeclock.entries]);
  const hoursText = usePayrollHoursText(hourMinutes);

  return (
    <>{locationEntry && createPortal(
      <TimeclockLocationModal
        isOpen
        onClose={() => setLocationEntry(null)}
        resourceName={`${data.resource.first_name} ${data.resource.last_name}`}
        clockIn={locationEntry.clock_in}
        clockOut={locationEntry.clock_out}
        clockInLat={locationEntry.clock_in_lat}
        clockInLng={locationEntry.clock_in_lng}
        clockOutLat={locationEntry.clock_out_lat}
        clockOutLng={locationEntry.clock_out_lng}
      />,
      document.body
    )}
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200/50 overflow-hidden touch-manipulation" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Arial, sans-serif' }}>
      <div 
        className="p-2.5 sm:p-4 active:bg-gray-50/50 transition-colors duration-150" 
        onClick={onToggle}
        style={{ minHeight: '44px' }}
      >
        {/* Header - iOS Style */}
        <div className="flex items-center justify-between mb-2 sm:mb-3">
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="relative h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-gray-100 flex items-center justify-center text-[13px] sm:text-[15px] font-semibold text-gray-700 overflow-hidden flex-shrink-0">
              {data.resource.avatar_url ? (
                <img 
                  src={data.resource.avatar_url} 
                  alt={`${data.resource.first_name} ${data.resource.last_name}`}
                  className="h-full w-full object-cover"
                />
              ) : (
                getPersonInitials(data.resource.first_name, data.resource.last_name)
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5">
                <h3 className="text-[15px] sm:text-[17px] font-semibold text-gray-900 truncate">
                  {data.resource.first_name} {data.resource.last_name}
                </h3>
                <span className={`px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-[11px] font-semibold rounded-md flex-shrink-0 ${
                  data.resource.compensation_type === 'hourly' 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-purple-100 text-purple-700'
                }`}>
                  {data.resource.compensation_type.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <p className="text-[12px] sm:text-[13px] text-gray-500 truncate">
                {data.resource.email}
                {data.resource.compensation_type === 'hourly' && data.resource.hourly_rate != null && (
                  <span className="text-blue-600 font-medium"> · ${data.resource.hourly_rate.toFixed(2)}/hr</span>
                )}
                {data.resource.compensation_type === 'flat_rate' && data.resource.flat_rate_amount != null && (
                  <span className="text-purple-600 font-medium"> · ${data.resource.flat_rate_amount.toFixed(2)} flat</span>
                )}
              </p>
            </div>
          </div>
          <div className="text-right flex-shrink-0 ml-2 sm:ml-3">
            {isLoading ? (
              <Loader2 className="h-6 w-6 sm:h-7 sm:w-7 animate-spin text-blue-600 ml-auto" aria-label="Loading earnings" />
            ) : (
              <>
                <div className="text-[20px] sm:text-[22px] font-bold text-gray-900 leading-tight">
                  ${data.totalEarnings.toFixed(2)}
                </div>
                <div className="text-[10px] sm:text-[11px] font-medium text-gray-500 mt-0.5">Total</div>
              </>
            )}
          </div>
        </div>

        {/* Stats Grid - iOS Style */}
        <div className="grid grid-cols-2 gap-1.5 sm:gap-2 mb-2 sm:mb-3">
          {/* Show Time metric with earnings if there are timeclock entries */}
          {data.timeclock.entries.length > 0 && (
            <div className="bg-gray-50 rounded-xl p-2 sm:p-3 border border-gray-100">
              <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1">
                <Clock className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600" />
                <span className="text-[10px] sm:text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Time</span>
                <span className="text-[10px] sm:text-[11px] font-semibold text-gray-600">
                  ${data.timeclock.totalEarnings.toFixed(2)}
                </span>
              </div>
              <div className="text-[14px] sm:text-[17px] font-bold text-gray-900">
                {hoursText(data.timeclock.entries.reduce((sum, entry) => sum + (entry.total_minutes || 0), 0))}
              </div>
            </div>
          )}
          
          {/* Show Flat Rate only if there are flat rate entries */}
          {Object.values(data.bookings).some(booking => 
            booking.items.some(item => item.entry_type === 'flat_rate' && item.earnings > 0)
          ) && (
            <div className="bg-gray-50 rounded-xl p-2 sm:p-3 border border-gray-100">
              <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1">
                <DollarSign className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-purple-600" />
                <span className="text-[10px] sm:text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Flat</span>
              </div>
              <div className="text-[14px] sm:text-[17px] font-bold text-gray-900">
                ${Object.values(data.bookings).reduce((sum, booking) => 
                  sum + booking.items.reduce((itemSum, item) => 
                    itemSum + (item.entry_type === 'flat_rate' ? item.earnings : 0), 
                    0
                  ), 0).toFixed(2)}
              </div>
            </div>
          )}
          
          {/* Show Commission only if there are commission entries */}
          {Object.values(data.bookings).some(booking => 
            booking.items.some(item => item.entry_type === 'commission' && item.earnings > 0)
          ) && (
            <div className="bg-gray-50 rounded-xl p-2 sm:p-3 border border-gray-100">
              <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1">
                <DollarSign className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-orange-600" />
                <span className="text-[10px] sm:text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Comm.</span>
              </div>
              <div className="text-[14px] sm:text-[17px] font-bold text-gray-900">
                ${Object.values(data.bookings).reduce((sum, booking) => 
                  sum + booking.items.reduce((itemSum, item) => 
                    itemSum + (item.entry_type === 'commission' ? item.earnings : 0), 
                    0
                  ), 0).toFixed(2)}
              </div>
            </div>
          )}
          
          {/* Show Bonus only if there are bonus entries */}
          {data.bonuses && data.bonuses.totalEarnings > 0 && (
            <div className="bg-gray-50 rounded-xl p-2 sm:p-3 border border-gray-100">
              <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1">
                <DollarSign className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-pink-600" />
                <span className="text-[10px] sm:text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Bonus</span>
              </div>
              <div className="text-[14px] sm:text-[17px] font-bold text-gray-900">
                ${(data.bonuses?.totalEarnings || 0).toFixed(2)}
              </div>
            </div>
          )}

          {data.adjustments && data.adjustments.entries.length > 0 && (
            <div className="bg-gray-50 rounded-xl p-2 sm:p-3 border border-gray-100">
              <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1">
                <DollarSign className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-slate-600" />
                <span className="text-[10px] sm:text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Adjustments</span>
              </div>
              <div className={`text-[14px] sm:text-[17px] font-bold ${data.adjustments.totalEarnings < 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {data.adjustments.totalEarnings < 0 ? '-' : ''}${Math.abs(data.adjustments.totalEarnings).toFixed(2)}
              </div>
            </div>
          )}

          {/* Show Tips only if there are tip entries */}
          {data.tips && data.tips.totalEarnings > 0 && (
            <div className="bg-gray-50 rounded-xl p-2 sm:p-3 border border-gray-100">
              <div className="flex items-center gap-1 sm:gap-1.5 mb-0.5 sm:mb-1">
                <DollarSign className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-yellow-600" />
                <span className="text-[10px] sm:text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Tips</span>
              </div>
              <div className="text-[14px] sm:text-[17px] font-bold text-gray-900">
                ${(data.tips?.totalEarnings || 0).toFixed(2)}
              </div>
            </div>
          )}
        </div>

        {/* Expand Indicator - iOS Style */}
        <div className="flex items-center justify-between pt-1.5 sm:pt-3 border-t border-gray-100">
          <p className="text-[11px] sm:text-[13px] text-gray-500">
            Tap to view details
          </p>
          <ChevronDown className={`h-4 w-4 sm:h-5 sm:w-5 text-gray-400 transition-transform duration-200 ${
            isExpanded ? 'rotate-180' : ''
          }`} />
        </div>
      </div>

      {/* Expanded Content - iOS Style */}
      {isExpanded && (
        <div className="border-t border-gray-100 bg-gray-50/30 p-4" onClick={(e) => e.stopPropagation()}>
          {/* Timeclock Entries - iOS Style */}
          <div className="mb-4 pb-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <Clock className="h-5 w-5 text-gray-600" />
                <div>
                  <h3 className="text-[17px] font-semibold text-gray-900">Time Clock</h3>
                  <p className="text-[13px] text-gray-500">
                    ${data.timeclock.totalEarnings.toFixed(2)} total
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-[15px] font-medium text-gray-600">
                  {hoursText(data.timeclock.entries.reduce((sum, entry) => sum + (entry.total_minutes || 0), 0))}
                </div>
                {onCreateEntry && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onCreateEntry(resourceId);
                    }}
                    className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-[13px] font-semibold active:bg-blue-700 transition-colors touch-manipulation"
                    style={{ minHeight: '32px' }}
                  >
                    <Plus className="h-4 w-4" />
                    Add Entry
                  </button>
                )}
              </div>
            </div>
             
            {data.timeclock.entries.length === 0 && onCreateEntry ? (
              <div className="bg-gray-50 rounded-xl p-4 border border-gray-200/50 text-center">
                <p className="text-[13px] text-gray-500 mb-3">No timeclock entries yet</p>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateEntry(resourceId);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg text-[14px] font-semibold active:bg-blue-700 transition-colors touch-manipulation"
                  style={{ minHeight: '36px' }}
                >
                  <div className="flex items-center justify-center gap-2">
                    <Plus className="h-4 w-4" />
                    Add Timeclock Entry
                  </div>
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {data.timeclock.entries.map((entry) => (
                  <div 
                    key={entry.id} 
                    className="bg-white rounded-xl p-3 border border-gray-200/50 active:bg-gray-50 transition-colors cursor-pointer touch-manipulation"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditEntry({
                        ...entry,
                        resource_first_name: data.resource.first_name,
                        resource_last_name: data.resource.last_name,
                        resource_email: data.resource.email,
                        compensation_type: data.resource.compensation_type,
                        flat_rate_amount: data.resource.flat_rate_amount,
                        hourly_rate: entry.hourly_rate ?? data.resource.hourly_rate,
                        entry_type: 'timeclock',
                        earnings: entry.earnings || 0
                      });
                    }}
                    style={{ minHeight: '44px' }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1.5">
                          <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                          <span className="text-[15px] font-medium text-gray-900">
                            {format(new Date(entry.clock_in), 'MMM d, yyyy')}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0 ${
                            payrollStatusClass(entry.process_status)
                          }`}>
                            {payrollStatusLabel(entry.process_status)}
                          </span>
                          {(entry.clock_in_lat != null || entry.clock_out_lat != null) && (
                            <button
                              onClick={(e) => { e.stopPropagation(); setLocationEntry(entry); }}
                              className="p-1 -m-1 rounded-lg hover:bg-blue-50 active:bg-blue-100 transition-colors"
                            >
                              <MapPin className="h-4 w-4 text-blue-500" />
                            </button>
                          )}
                          {entry.needs_review && (
                            <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0 bg-amber-100 text-amber-800">
                              NEEDS REVIEW
                            </span>
                          )}
                        </div>
                        {entry.needs_review && (
                          <div className="flex items-center gap-2 mb-1.5 text-[12px] text-amber-800">
                            <span>{entry.review_reason || 'Flagged for review'}</span>
                            {onConfirmEntry && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); void onConfirmEntry(entry.id); }}
                                className="px-2 py-0.5 rounded-md bg-amber-600 text-white font-semibold hover:bg-amber-700 active:bg-amber-800"
                              >
                                Confirm as correct
                              </button>
                            )}
                          </div>
                        )}
                        
                        <div className="text-[13px] text-gray-600 mb-1">
                          {format(new Date(entry.clock_in), 'h:mm a')} - {
                            entry.clock_out ? format(new Date(entry.clock_out), 'h:mm a') : 'In Progress'
                          }
                        </div>
                        
                        {entry.break_start && (
                          <div className="text-[12px] text-gray-500">
                            Break: {format(new Date(entry.break_start), 'h:mm a')} - {
                              entry.break_end ? format(new Date(entry.break_end), 'h:mm a') : 'In Progress'
                            }
                          </div>
                        )}
                      </div>
                      
                      <div className="text-right ml-3">
                        <div className="text-[17px] font-bold text-gray-900 whitespace-nowrap">
                          {hoursText(entry.total_minutes || 0)}
                        </div>
                        <div className="text-[13px] font-semibold text-gray-600 whitespace-nowrap">
                          ${(entry.earnings || 0).toFixed(2)}
                        </div>
                        {((entry.overtime_minutes || 0) + (entry.double_time_minutes || 0)) > 0 && (
                          <div className="text-[11px] font-semibold text-orange-600 whitespace-nowrap">
                            incl. {hoursText((entry.overtime_minutes || 0) + (entry.double_time_minutes || 0))} OT
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

         {/* Bonus Entries - iOS Style */}
         {data.bonuses && (
           <div className="mb-4 pb-4 border-b border-gray-100">
             <div className="flex items-center justify-between mb-4">
               <div className="flex items-center gap-2.5">
                 <DollarSign className="h-5 w-5 text-green-600" />
                 <div>
                   <h3 className="text-[17px] font-semibold text-gray-900">Bonuses</h3>
                   <p className="text-[13px] text-gray-500">
                     ${data.bonuses.totalEarnings.toFixed(2)} total
                   </p>
                 </div>
               </div>
               <div className="flex items-center gap-3">
                 <div className="text-[15px] font-medium text-gray-600">
                   {data.bonuses.entries.length} bonus{data.bonuses.entries.length !== 1 ? 'es' : ''}
                 </div>
                 {onCreateBonus && (
                   <button
                     onClick={(e) => {
                       e.stopPropagation();
                       onCreateBonus(resourceId);
                     }}
                     className="flex items-center justify-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-[13px] font-semibold active:bg-blue-700 transition-colors touch-manipulation"
                     style={{ minHeight: '32px' }}
                   >
                     <Plus className="h-4 w-4" />
                     Add bonus
                   </button>
                 )}
               </div>
             </div>
             {data.bonuses.entries.length === 0 && (
               <p className="text-[13px] text-gray-500">No bonuses yet</p>
             )}
            
            <div className="space-y-2">
              {data.bonuses.entries.map((entry) => (
                <div 
                  key={entry.id} 
                  className="bg-white rounded-xl p-3 border border-gray-200/50 active:bg-gray-50 transition-colors cursor-pointer touch-manipulation"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditEntry({
                      ...entry,
                      resource_id: resourceId,
                      resource_first_name: data.resource.first_name,
                      resource_last_name: data.resource.last_name,
                      resource_email: data.resource.email,
                      compensation_type: data.resource.compensation_type,
                      flat_rate_amount: data.resource.flat_rate_amount,
                      hourly_rate: data.resource.hourly_rate,
                      entry_type: 'bonus',
                      booking_number: entry.booking_number,
                      booking_title: entry.booking_title,
                      start_date: entry.created_at,
                      end_date: entry.created_at
                    });
                  }}
                  style={{ minHeight: '44px' }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-[15px] font-medium text-gray-900">
                          {formatDate(entry.created_at)}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-100 text-green-700 flex-shrink-0">
                          BONUS
                        </span>
                        {entry.process_status && (
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0 ${
                            payrollStatusClass(entry.process_status)
                          }`}>
                            {payrollStatusLabel(entry.process_status)}
                          </span>
                        )}
                      </div>
                      
                      <div className="text-[13px] text-gray-600 mb-1">
                        {entry.item_name || 'Booking Creation Bonus'}
                      </div>
                      
                      {entry.item_description && (
                        <div className="text-[12px] text-gray-500 mb-1">
                          {entry.item_description}
                        </div>
                      )}
                      
                      {entry.booking_number && (
                        <div className="text-[12px] text-gray-500">
                          Booking #{entry.booking_number}
                        </div>
                      )}
                    </div>
                    
                    <div className="text-right ml-3">
                      <div className="text-[17px] font-bold text-green-600">
                        ${(entry.earnings || 0).toFixed(2)}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        Bonus
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pay adjustments (corrections to shifts that were already paid) */}
        {data.adjustments && data.adjustments.entries.length > 0 && (
          <div className="mb-4 pb-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <DollarSign className="h-5 w-5 text-slate-600" />
                <div>
                  <h3 className="text-[17px] font-semibold text-gray-900">Adjustments</h3>
                  <p className="text-[13px] text-gray-500">
                    {data.adjustments.totalEarnings < 0 ? '-' : ''}${Math.abs(data.adjustments.totalEarnings).toFixed(2)} total
                  </p>
                </div>
              </div>
              <div className="text-[15px] font-medium text-gray-600">
                {data.adjustments.entries.length} correction{data.adjustments.entries.length !== 1 ? 's' : ''}
              </div>
            </div>
            <div className="space-y-2">
              {data.adjustments.entries.map((entry) => (
                <div key={entry.id} className="bg-white rounded-xl p-3 border border-gray-200/50" style={{ minHeight: '44px' }}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-[15px] font-medium text-gray-900">{formatDate(entry.created_at)}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0 ${
                          payrollStatusClass(entry.process_status)
                        }`}>
                          {payrollStatusLabel(entry.process_status)}
                        </span>
                      </div>
                      <div className="text-[13px] text-gray-600 mb-1">{entry.item_name || 'Pay adjustment'}</div>
                      {entry.item_description && <div className="text-[12px] text-gray-500 mb-1">{entry.item_description}</div>}
                      {entry.notes && <div className="text-[12px] text-gray-500">{entry.notes}</div>}
                    </div>
                    <div className="text-right ml-3">
                      <div className={`text-[17px] font-bold ${(entry.earnings || 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>
                        {(entry.earnings || 0) < 0 ? '-' : '+'}${Math.abs(entry.earnings || 0).toFixed(2)}
                      </div>
                      <div className="text-[11px] text-gray-500">Adjustment</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tip Entries - iOS Style */}
        {data.tips && data.tips.entries.length > 0 && (
          <div className="mb-4 pb-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2.5">
                <DollarSign className="h-5 w-5 text-yellow-600" />
                <div>
                  <h3 className="text-[17px] font-semibold text-gray-900">Tips</h3>
                  <p className="text-[13px] text-gray-500">
                    ${data.tips.totalEarnings.toFixed(2)} total
                  </p>
                </div>
              </div>
              <div className="text-[15px] font-medium text-gray-600">
                {data.tips.entries.length} tip{data.tips.entries.length !== 1 ? 's' : ''}
              </div>
            </div>

            <div className="space-y-2">
              {data.tips.entries.map((entry) => (
                <div
                  key={entry.id}
                  className="bg-white rounded-xl p-3 border border-gray-200/50 active:bg-gray-50 transition-colors cursor-pointer touch-manipulation"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditEntry({
                      ...entry,
                      resource_id: resourceId,
                      resource_first_name: data.resource.first_name,
                      resource_last_name: data.resource.last_name,
                      resource_email: data.resource.email,
                      compensation_type: data.resource.compensation_type,
                      flat_rate_amount: data.resource.flat_rate_amount,
                      hourly_rate: data.resource.hourly_rate,
                      entry_type: 'tip',
                      booking_number: entry.booking_number,
                      booking_title: entry.booking_title,
                      start_date: entry.created_at,
                      end_date: entry.created_at
                    });
                  }}
                  style={{ minHeight: '44px' }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1.5">
                        <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-[15px] font-medium text-gray-900">
                          {formatDate(entry.created_at)}
                        </span>
                        <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-yellow-100 text-yellow-700 flex-shrink-0">
                          TIP
                        </span>
                        {entry.process_status && (
                          <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold flex-shrink-0 ${
                            payrollStatusClass(entry.process_status)
                          }`}>
                            {payrollStatusLabel(entry.process_status)}
                          </span>
                        )}
                      </div>

                      <div className="text-[13px] text-gray-600 mb-1">
                        {entry.item_name || 'Customer Tip'}
                      </div>

                      {entry.item_description && (
                        <div className="text-[12px] text-gray-500 mb-1">
                          {entry.item_description}
                        </div>
                      )}

                      {entry.booking_number && (
                        <div className="text-[12px] text-gray-500">
                          Booking #{entry.booking_number}
                        </div>
                      )}
                    </div>

                    <div className="text-right ml-3">
                      <div className="text-[17px] font-bold text-yellow-600">
                        ${(entry.earnings || 0).toFixed(2)}
                      </div>
                      <div className="text-[11px] text-gray-500">
                        Tip
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Booking Entries - iOS Style */}
        {Object.entries(data.bookings).map(([bookingId, booking]) => (
          <div key={bookingId} className="border-b border-gray-100 last:border-b-0">
            <div 
              className="px-4 py-3.5 flex items-center justify-between active:bg-gray-50/50 transition-colors touch-manipulation"
              onClick={(e) => {
                e.stopPropagation();
                onToggleBooking(`${resourceId}-${bookingId}`);
              }}
              style={{ minHeight: '44px' }}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <Calendar className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-[15px] text-gray-900 truncate">
                    Job #{booking.booking_number} - {booking.booking_title}
                  </div>
                  {booking.end_date && (
                    <div className="text-[13px] text-gray-500 mt-0.5">
                      Completed {format(parseISO(booking.end_date), 'MMM d, yyyy')}
                    </div>
                  )}
                  <div className="text-[13px] text-gray-600 mt-0.5">
                    {booking.totalHours.toFixed(1)}h • ${booking.totalEarnings.toFixed(2)}
                  </div>
                </div>
              </div>
              <ChevronDown 
                className={`h-5 w-5 text-gray-400 transition-transform duration-200 flex-shrink-0 ${
                  expandedBookings.has(`${resourceId}-${bookingId}`) ? 'rotate-180' : ''
                }`}
              />
            </div>
            
            {expandedBookings.has(`${resourceId}-${bookingId}`) && (
              <div className="bg-white/40 backdrop-blur-sm">
                {/* Desktop Table */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="min-w-full table-fixed">
                    <colgroup>
                      <col className="w-[22%]" />
                      <col className="w-[13%]" />
                      <col className="w-[9%]" />
                      <col className="w-[12%]" />
                      <col className="w-[34%]" />
                      <col className="w-[10%]" />
                    </colgroup>
                    <thead className="bg-white/60 backdrop-blur-sm">
                      <tr>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Item
                        </th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Hrs
                        </th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Rate
                        </th>
                        <th className="px-2 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Total
                        </th>
                        <th className="px-2 py-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100/50">
                      {booking.items.map((item) => (
                        <tr 
                          key={item.id} 
                          className="hover:bg-white/60 active:bg-gray-50 transition-colors duration-150 cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditEntry({
                              ...item,
                              resource_id: resourceId,
                              resource_first_name: data.resource.first_name,
                              resource_last_name: data.resource.last_name,
                              resource_email: data.resource.email,
                              compensation_type: data.resource.compensation_type,
                              flat_rate_amount: data.resource.flat_rate_amount,
                              hourly_rate: data.resource.hourly_rate,
                              booking_number: booking.booking_number,
                              booking_title: booking.booking_title,
                              start_date: booking.start_date,
                              end_date: booking.end_date
                            });
                          }}
                          style={{ minHeight: '44px' }}
                        >
                          <td className="px-2 py-2">
                            <div className="text-xs font-medium text-gray-900 truncate" title={item.item_name}>
                              {item.item_name}
                            </div>
                            {item.item_description && (
                              <div className="text-xs text-gray-500 mt-0.5 truncate" title={item.item_description}>
                                {item.item_description}
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-600 whitespace-nowrap">
                            {format(new Date(item.completed_at || item.created_at), 'MMM d')}
                          </td>
                          <td className="px-2 py-2 text-xs font-medium text-gray-900">
                            {item.hours.toFixed(1)}h
                          </td>
                          <td className="px-2 py-2 text-xs text-gray-600 whitespace-nowrap">
                            {item.entry_type === 'commission' 
                              ? 'Comm.'
                              : `$${(item.rate_amount ?? (item.compensation_type === 'flat_rate' ? item.flat_rate_amount : item.hourly_rate) ?? 0).toFixed(2)}/hr`
                            }
                          </td>
                          <td className="px-2 py-2">
                            <div className="flex items-center gap-2">
                              <div className="text-xs font-bold text-gray-900 whitespace-nowrap">
                                ${(() => {
                                  return (item.earnings || 0).toFixed(2);
                                })()}
                              </div>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${
                                payrollStatusClass(item.process_status)
                              }`}>
                                {payrollStatusLabel(item.process_status)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile/Tablet Cards */}
                <div className="lg:hidden p-4 space-y-3">
                  {booking.items.map((item) => (
                    <div 
                      key={item.id} 
                      className="bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-white/30 active:bg-gray-50 transition-colors cursor-pointer touch-manipulation"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditEntry({
                          ...item,
                          resource_id: resourceId,
                          resource_first_name: data.resource.first_name,
                          resource_last_name: data.resource.last_name,
                          resource_email: data.resource.email,
                          compensation_type: data.resource.compensation_type,
                          flat_rate_amount: data.resource.flat_rate_amount,
                          hourly_rate: data.resource.hourly_rate,
                          booking_number: booking.booking_number,
                          booking_title: booking.booking_title,
                          start_date: booking.start_date,
                          end_date: booking.end_date
                        });
                      }}
                      style={{ minHeight: '44px' }}
                    >
                      <div className="flex justify-between items-start mb-3">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-gray-900 text-sm truncate">{item.item_name}</h4>
                          {item.item_description && (
                            <p className="text-xs text-gray-500 mt-1">{item.item_description}</p>
                          )}
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ml-3 ${
                          payrollStatusClass(item.process_status)
                        }`}>
                          {payrollStatusLabel(item.process_status)}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                          <span className="text-gray-500">Date:</span>
                          <div className="font-medium text-gray-900">
                            {formatDate(item.completed_at || item.created_at)}
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">Hours:</span>
                          <div className="font-medium text-gray-900">{item.hours.toFixed(1)}h</div>
                        </div>
                        <div>
                          <span className="text-gray-500">Rate:</span>
                          <div className="font-medium text-gray-900">
                            {item.entry_type === 'commission' 
                              ? 'Commission'
                              : `$${(item.rate_amount ?? (item.compensation_type === 'flat_rate' ? item.flat_rate_amount : item.hourly_rate) ?? 0).toFixed(2)}/hr`
                            }
                          </div>
                        </div>
                        <div>
                          <span className="text-gray-500">Total:</span>
                          <div className="font-bold text-gray-900">
                            ${(() => {
                              return (item.earnings || 0).toFixed(2);
                            })()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
        </div>
      )}
    </div>
    </>
  );
}